import { useMemo, useState } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material'
import { ActivityIcon, CalculatorIcon, GlobeIcon, LinkIcon, LockIcon, MousePointerClickIcon, RadioIcon, UserPlusIcon } from 'lucide-react'

import { Button, Card, Skeleton, Tabs, TABS_CONTENT_GAP } from '@repo/ui'
import { EmptyState, Header } from 'components'
import { AnalyticsBlock } from 'components/display/Analytics'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { useCanAccess } from 'hooks'
import { useWebsiteOverview } from 'hooks/api'
import { legacyAnalyticsTabTarget } from 'routes'
import { relativeOrDateTime } from 'utils'

import { SourceNotices } from './components/shared'
import { WebsiteFunnelCard, WebsiteFunnelSkeleton } from './components/WebsiteFunnelCard'
import { WebsiteLeadsCard, WebsiteLiveCard } from './components/WebsiteLiveCards'
import { WebsiteVisitorHistoryCard } from './components/WebsiteVisitorHistoryCard'
import { WebsiteLiveMapCard, WebsiteMapCard } from './components/WebsiteMapCard'
import { WebsiteFlowsCard } from './components/WebsiteFlowsCard'
import { WebsitePagesCard } from './components/WebsitePagesCard'
import { WebsiteSourcesCard } from './components/WebsiteSourcesCard'
import { WebsiteVitalsCard } from './components/WebsiteVitalsCard'
// Invite links (2026-10-09) — shared with Internal → People.
import { TrackedLinksCard } from '../TrackedLinks/TrackedLinksCard'
import {
  AUDIENCE_LABELS,
  formatCount,
  initialRangePreset,
  RANGE_PRESETS,
  RANGE_STORAGE_KEY,
  rangeForPreset,
  type RangePreset,
} from './websiteAnalyticsModel'
import { ADMIN_ONLY_TABS, WEBSITE_TABS, websiteTabFrom, type WebsiteTab } from './websiteTabs'

/**
 * `/internal/analytics/website` (Analytics → Website; was `/internal/analytics` until VRG-684) —
 * VRG-640: how vera.inc visitors become clients.
 *
 * Tours moved to `/internal/usage` (Tours + Ask), which is what freed this page. It used
 * to be an empty "Commercial analytics are not built yet" state; it now carries the
 * website funnel.
 *
 * SEO & AEO LEFT IN VRG-672. VRG-643 added it as a second tab; it moved to
 * `/internal/gtm/seo` when the GTM sidebar group was created (Spencer, 2026-10-06): how
 * vera.inc is FOUND is a go-to-market channel, and its Recommendations queue is a desk
 * someone works rather than a figure someone reads. The old `?tab=seo` address redirects
 * (below) with every other param intact, because the review emails already sent link to it.
 * VRG-665 then added Affiliates (Refer a brand and Vera Insiders) as a tab, and VRG-678
 * moved it to `/internal/gtm/insiders` for the same reason: it is Traction channel #14. Both
 * old `?tab=` addresses redirect with every other param intact. VRG-712 brought a tab bar back,
 * this time over Website's own views (Overview / Live / Visitors / Tracked links, below), not
 * over other subjects. There is still no subtitle line (VRG-689): the range row says what each
 * tab counts.
 *
 * THREE POPULATIONS, and every number says which it counts (task Decisions §3):
 *   All visitors (Vercel)                          — cookieless, everyone
 *   Visitors with analytics on (PostHog)           — the subset PostHog may record, per person
 *     (outside the US after they accept; in the US by default unless they opt out or send GPC)
 *   Signups to clients (Vera)                      — vera-2's own DB
 *
 * WHO SEES WHAT. The page gate is `internal_analytics_view` (routes/internalAccess.ts).
 * Visitor history and Lead journeys show emails, so they render for platform_admin only — and,
 * the part that matters, their endpoints refuse everyone else server-side
 * (`website-analytics-internal.controller.ts`). Hiding them here is the courtesy; the
 * 403 is the control. Live now renders for the page gate, and the API nulls the email
 * for non-admins.
 */

const RANGE_OPTIONS: readonly ViewToggleOption<RangePreset>[] = RANGE_PRESETS.map((p) => ({
  value: p.value,
  label: p.label,
}))

/** The viewer's last range choice. Storage can be absent or throw (private mode): never fatal. */
const readStoredRange = (): string | null => {
  try {
    return window.localStorage.getItem(RANGE_STORAGE_KEY)
  } catch {
    return null
  }
}
const writeStoredRange = (preset: RangePreset) => {
  try {
    window.localStorage.setItem(RANGE_STORAGE_KEY, preset)
  } catch {
    // A preference that cannot be saved is just not remembered.
  }
}

/**
 * VRG-689: FIVE headline tiles on one row at a 1280px viewport (each ~176px with the sidebar
 * open; every label fits, measured — §49), three a row from `md`, two from `sm`, one on a
 * phone. At 1024 five a row left ~125px tiles, which wrapped every label word by word, so the
 * five-up starts at the same 1280 line the funnel's one-row layout does. A CSS grid rather than
 * MUI's 12-column Grid, which cannot split a row in five.
 */
export const KPI_GRID_SX = {
  display: 'grid',
  gap: '16px',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
  },
  '@media (min-width:1280px)': { gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' },
} as const

const PageSkeleton = () => (
  <Stack spacing={6}>
    <Box sx={KPI_GRID_SX}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Card sx={{ p: 6 }} key={i}>
          <Skeleton height={16} width="60%" />
          <Skeleton height={32} width="40%" />
        </Card>
      ))}
    </Box>
    {/* The funnel mirrors its coverage line, chart and handoff (§19); the rest (Pages, Site flow, Map,
        Sources) are plain card skeletons. */}
    <WebsiteFunnelSkeleton />
    {[0, 1, 2, 3].map((i) => (
      <Card key={i} title=" ">
        <Stack spacing={3} sx={{ mt: 4 }}>
          <Skeleton height={14} lines={5} />
        </Stack>
      </Card>
    ))}
  </Stack>
)

const TAB_LABELS: Record<WebsiteTab, string> = {
  overview: 'Overview',
  live: 'Live',
  visitors: 'Visitors',
  links: 'Tracked links',
}

/** "the last 7 days", "today": how the notes name the selected range. */
const rangePhrase = (preset: RangePreset): string => {
  const label = RANGE_PRESETS.find((p) => p.value === preset)?.label ?? ''
  return preset === 'today' ? 'today' : `the last ${label.toLowerCase()}`
}

/**
 * VRG-712 (Spencer, 2026-10-16: "Tracked links are too far down"; "Should we have a tab for live,
 * historical as well?"; "We need a divider between live now and historical") — the page is four
 * tabs, the tab in the URL (`?tab=`, every other param kept, as on GTM → Sales):
 *
 *   Overview       KPI tiles, funnel, site flow, pages, sources, map          (date range)
 *   Live           the live map + Live now                                    (right now)
 *   Visitors       visitor history, paged on the server, + the journey drawer (date range)
 *                  Lead journeys under it (vera-2's own record; not range-cut)
 *   Tracked links  every tracked link, clicks counted in the range            (date range)
 *
 * Visitors and Tracked links are platform-admin surfaces (their endpoints refuse everyone else),
 * so for anyone else the tabs are not there at all (§27: hide surfaces you cannot reach).
 */
export const InternalAnalyticsPage = () => {
  const { search } = useLocation()
  // `/internal/analytics?tab=seo&seo=…` is where SEO & AEO lived until VRG-672, and
  // `?tab=affiliates` where the Affiliates dashboard lived until VRG-678. Since VRG-684 those
  // addresses are caught one step earlier (`analyticsIndexTarget`, App.tsx) and never reach this
  // page; the check stays for a `?tab=` appended to the Website address itself. `replace`, so the
  // dead address is not left in history for Back to bounce off.
  const movedTarget = legacyAnalyticsTabTarget(search)
  const { isHasAccess: isPlatformAdmin } = useCanAccess({ role: 'platform_admin' })
  const [params, setParams] = useSearchParams()
  const tab = websiteTabFrom(params.get('tab'), isPlatformAdmin)
  const setTab = (value: string) => {
    const next = new URLSearchParams(params)
    next.set('tab', value)
    setParams(next, { replace: true })
  }
  const tabs = WEBSITE_TABS.filter((t) => isPlatformAdmin || !ADMIN_ONLY_TABS.has(t)).map((value) => ({
    value,
    label: TAB_LABELS[value],
  }))
  // Invite links: the header button and the Tracked links card share one dialog, which lives in
  // the Tracked links tab. From any other tab the header button opens that tab first, so the new
  // link is seen landing in the list.
  const [createLinkOpen, setCreateLinkOpen] = useState(false)
  const openCreateLink = () => {
    if (tab !== 'links') setTab('links')
    setCreateLinkOpen(true)
  }
  if (movedTarget) return <Navigate to={movedTarget} replace />

  return (
    <>
      {/* VRG-684 — the page is the Website row of the Analytics section, and is titled for it. */}
      <Header
        title="Website"
        rightComponents={
          isPlatformAdmin ? (
            // Hidden on a phone, where the header has room for one control: the Tracked links
            // card carries the same button.
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Button variant="secondary" onClick={openCreateLink} startIcon={<LinkIcon strokeWidth={1} />}>
                Create tracked link
              </Button>
            </Box>
          ) : undefined
        }
      />
      <Tabs redirect={false} tabs={tabs} value={tab} onChange={setTab} />
      <Box sx={{ mt: `${TABS_CONTENT_GAP}px` }} data-testid={`website-tab-${tab}`}>
        <WebsitePanel tab={tab} createLinkOpen={createLinkOpen} onCreateLinkOpenChange={setCreateLinkOpen} />
      </Box>
    </>
  )
}

/** A titled band inside Overview: a rule, the heading, and one line on what the band counts. */
const OverviewSection = ({ title, note, children, testId }: { title: string; note: string; children: React.ReactNode; testId: string }) => (
  <Stack spacing={4} useFlexGap data-testid={testId}>
    <Stack spacing={1}>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {note}
      </Typography>
    </Stack>
    {children}
  </Stack>
)

const WebsitePanel = ({
  tab,
  createLinkOpen,
  onCreateLinkOpenChange,
}: {
  tab: WebsiteTab
  createLinkOpen: boolean
  onCreateLinkOpenChange: (open: boolean) => void
}) => {
  const theme = useTheme()
  const [searchParams, setSearchParams] = useSearchParams()

  // The range lives in the URL so a view is linkable and survives the Signups round trip;
  // with no `?range=`, the viewer's last choice (localStorage), else 7 days.
  const preset: RangePreset = initialRangePreset(searchParams.get('range'), readStoredRange())
  const range = useMemo(() => rangeForPreset(preset), [preset])
  const setPreset = (next: RangePreset) => {
    writeStoredRange(next)
    const params = new URLSearchParams(searchParams)
    params.set('range', next)
    setSearchParams(params, { replace: true })
  }

  // The overview query runs only on Overview: Live, Visitors and Tracked links have their own.
  const { data, isLoading, error } = useWebsiteOverview(range, tab === 'overview')
  const status = (error as (Error & { status?: number }) | null)?.status

  // VRG-712: the range row says what it applies to, and Live says it ignores it. The freshness
  // line (VRG-689) is Overview's: the other tabs' reads carry their own cache.
  const freshness = tab === 'overview' && data ? `Computed ${relativeOrDateTime(data.computedAt)} · cached for 5 minutes` : null
  const rangeRow =
    tab === 'live' ? (
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 6 }} data-testid="website-live-now-note">
        <RadioIcon size={16} strokeWidth={1} color={theme.palette.text.secondary} />
        <Typography variant="body3" color="text.secondary">
          Right now: visitors active in the last 5 minutes, refreshed every 30 seconds. The date range on the other tabs does not
          apply here.
        </Typography>
      </Stack>
    ) : (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        spacing={3}
        sx={{ mb: 6 }}
      >
        <Typography variant="body3" color="text.secondary" data-testid="website-freshness">
          {freshness ?? (tab === 'visitors' ? 'Visits in the selected range.' : 'Clicks and interests counted in the selected range.')}
        </Typography>
        <ViewToggle options={RANGE_OPTIONS} value={preset} onChange={setPreset} ariaLabel="Date range" />
      </Stack>
    )

  if (tab === 'live')
    return (
      <>
        {rangeRow}
        {/* Live first, independent of the range: VRG-690 keeps the Live now list directly under
            the live map, so "who is on the site" reads as one block. The VRG-699 world frame
            measures its own width when it mounts, so it frames correctly in a tab shown later. */}
        <Stack spacing={6} useFlexGap>
          <WebsiteLiveMapCard />
          <WebsiteLiveCard />
        </Stack>
      </>
    )

  if (tab === 'visitors')
    return (
      <>
        {rangeRow}
        {/* VRG-712 (Spencer, 2026-10-16): Lead journeys sits under Visitor history, so every
            email-level surface is on this admin-only tab and Overview is aggregates only. Its
            figures are vera-2's own record, not cut by the range, and its note says so. */}
        <Stack spacing={6} useFlexGap>
          <WebsiteVisitorHistoryCard range={range} rangeLabel={rangePhrase(preset)} />
          <Box data-testid="website-lead-journeys">
            <WebsiteLeadsCard />
          </Box>
        </Stack>
      </>
    )

  if (tab === 'links')
    return (
      <>
        {rangeRow}
        <TrackedLinksCard
          createOpen={createLinkOpen}
          onCreateOpenChange={onCreateLinkOpenChange}
          range={range}
          rangeLabel={rangePhrase(preset)}
        />
      </>
    )

  return (
    <>
      {rangeRow}
      {isLoading ? (
        <PageSkeleton />
      ) : error || !data ? (
        <Card>
          <EmptyState
            icon={<LockIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />}
            title={
              status === 403
                ? 'You cannot view website analytics'
                : 'Could not load website analytics'
            }
            text={
              status === 403
                ? 'Website analytics needs the "View internal analytics" permission. Ask a platform admin to grant it.'
                : 'The request failed. Reload the page, or check the API is running.'
            }
            sx={{ py: 10 }}
          />
        </Card>
      ) : (
        <Stack spacing={10} useFlexGap>
          <Stack spacing={6} useFlexGap data-testid="website-section-funnel">
            <SourceNotices
              sources={[
                [AUDIENCE_LABELS.all, data.sources.vercel],
                [AUDIENCE_LABELS.consented, data.sources.posthog],
                [AUDIENCE_LABELS.vera, data.sources.vera],
              ]}
            />
            {/* Headline strip (Spencer, 2026-10-10; VRG-689 2026-10-13): visitors, sessions,
                leads, ROI models, waitlist — each naming the population it counts. Leads are
                submitted "Get started" forms (Vercel); ROI submitted and Waitlist joined are
                vera-2's own records. ROI submissions are NOT inside Leads: the calculator fires
                its own event and posts its own record (vera-website RoiCalculator.tsx), though
                one person may have done both. */}
            <Box sx={KPI_GRID_SX} data-testid="website-kpi-tiles">
              <AnalyticsBlock
                title="Visitors"
                term="website-all-visitors"
                icon={<GlobeIcon size={16} strokeWidth={1} />}
                stat={formatCount(data.allVisitors.visitors)}
                unit={AUDIENCE_LABELS.all}
              />
              <AnalyticsBlock
                title="Sessions"
                term="website-consented-visitors"
                icon={<ActivityIcon size={16} strokeWidth={1} />}
                stat={formatCount(data.consented?.sessions ?? null)}
                unit={AUDIENCE_LABELS.consented}
              />
              <AnalyticsBlock
                title="Leads"
                term="website-form-submitted"
                icon={<MousePointerClickIcon size={16} strokeWidth={1} />}
                stat={formatCount(data.allVisitors.events?.request_access_submitted ?? null)}
                unit={`Forms submitted · ${AUDIENCE_LABELS.all}`}
              />
              <AnalyticsBlock
                title="ROI submitted"
                term="website-roi-submitted"
                icon={<CalculatorIcon size={16} strokeWidth={1} />}
                stat={formatCount(data.vera?.roiSubmitted ?? null)}
                unit="ROI models received · Vera"
              />
              <AnalyticsBlock
                title="Waitlist joined"
                term="website-waitlist-joined"
                icon={<UserPlusIcon size={16} strokeWidth={1} />}
                stat={formatCount(data.vera?.waitlist ?? null)}
                unit="Waitlist requests · Vera"
              />
            </Box>
            <WebsiteFunnelCard overview={data} range={range} />
          </Stack>

          {/* VRG-712 (Spencer: "Sources should be a table below pages, and pages below site
              flow"): Site flow → Pages → Sources, each band titled. Pages and Site flow load on
              their own queries, each with its own skeleton. */}
          <OverviewSection
            title="Paths through the site"
            note={`Where visitors went, page to page, and how each page performed. ${AUDIENCE_LABELS.consented} for the flow and times; ${AUDIENCE_LABELS.all} for page views.`}
            testId="website-section-paths"
          >
            <WebsiteFlowsCard range={range} />
            <WebsitePagesCard range={range} />
          </OverviewSection>

          {/* VRG-762 — how fast the site was for the people on those paths. Placed after
              Pages and before Sources deliberately: it answers a question about the SAME
              pages the band above ranks ("that is the busiest page — is it also the slow
              one?"), and it is the one band here with a single population, which its own
              note says rather than leaving the reader to infer. */}
          <OverviewSection
            title="How fast it was for them"
            note={`Core Web Vitals as real visitors experienced them, per page and per device. ${AUDIENCE_LABELS.all} — the only band on this page with one population, because a consented-only speed figure would measure a self-selected slice.`}
            testId="website-section-speed"
          >
            <WebsiteVitalsCard range={range} />
          </OverviewSection>

          <OverviewSection
            title="Where visitors came from"
            note="Referrers, campaigns, landing pages and devices, then where in the world."
            testId="website-section-sources"
          >
            <WebsiteSourcesCard overview={data} />
            <WebsiteMapCard overview={data} />
          </OverviewSection>
        </Stack>
      )}

    </>
  )
}
